import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, Settings, User, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { logout, getCurrentUser, AuthUser } from "@/services/authService";

export default function UserMenu() {
  const [, setLocation] = useLocation();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Obtener usuario del servicio de autenticación
    const currentUser = getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    }
  }, []);

  const handleLogout = async () => {
    setIsLoading(true);

    try {
      // Llamar al servicio de logout
      await logout();
      toast.success("Sesión cerrada correctamente");
      // Redirigir a login
      window.location.href = "/login";
    } catch (error) {
      console.error("Error during logout:", error);
      toast.error("Error al cerrar sesión");
      setIsLoading(false);
    }
  };

  // Si no hay usuario, mostrar botón de login
  if (!user) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setLocation("/login")}
        className="text-slate-300 hover:text-white hover:bg-white/10"
      >
        Iniciar Sesión
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 text-slate-200 hover:text-white transition"
        >
          {/* Avatar */}
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
            {user.first_name?.charAt(0).toUpperCase() || user.username.charAt(0).toUpperCase()}
          </div>

          {/* Nombre */}
          <span className="hidden sm:inline text-sm font-medium">
            {user.first_name || user.username}
          </span>

          {/* Chevron */}
          <ChevronDown className="w-4 h-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-white/20">
        {/* Información del usuario */}
        <div className="px-4 py-3 border-b border-white/10">
          <p className="text-sm font-semibold text-white">
            {user.first_name && user.last_name 
              ? `${user.first_name} ${user.last_name}` 
              : user.first_name || user.username}
          </p>
          {user.email && <p className="text-xs text-slate-400 mt-1">{user.email}</p>}
          {user.role_name && (
            <div className="mt-2 inline-block px-2 py-1 bg-blue-500/20 border border-blue-500/30 rounded text-xs text-blue-300 font-medium">
              {user.role_name.toUpperCase()}
            </div>
          )}
        </div>

        <DropdownMenuSeparator className="bg-white/10" />

        {/* Opciones */}
        <DropdownMenuItem
          onClick={() => setLocation("/settings")}
          className="cursor-pointer hover:bg-white/10 focus:bg-white/10 text-slate-200 hover:text-white"
        >
          <Settings className="w-4 h-4 mr-2" />
          <span>Configuración</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => setLocation("/profile")}
          className="cursor-pointer hover:bg-white/10 focus:bg-white/10 text-slate-200 hover:text-white"
        >
          <User className="w-4 h-4 mr-2" />
          <span>Mi Perfil</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-white/10" />

        {/* Logout - Botón destacado */}
        <DropdownMenuItem
          onClick={handleLogout}
          disabled={isLoading}
          className="cursor-pointer hover:bg-red-500/20 focus:bg-red-500/20 text-red-400 hover:text-red-300 disabled:opacity-50"
        >
          <LogOut className="w-4 h-4 mr-2" />
          <span>{isLoading ? "Cerrando sesión..." : "Cerrar Sesión"}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
